import React, { useState, useEffect, useRef, useCallback } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Loader2, Wand2, Sparkles, Coins, CheckCircle2, X } from "lucide-react";

const POV_OPTIONS = [
  "First person, past tense",
  "First person, present tense",
  "Third person limited, past tense",
  "Third person, single POV",
  "Third person, dual POV",
  "Third person omniscient",
];
const LENGTHS = [
  { v: "scene", label: "Scene", hint: "~350–500 words" },
  { v: "short", label: "Standard", hint: "~700–1000 words" },
  { v: "full", label: "Full", hint: "~1300–1700 words" },
];
const BOOK_SIZES = [
  { v: 6, label: "Novelette", hint: "~6 ch" },
  { v: 12, label: "Novella", hint: "~12 ch" },
  { v: 24, label: "Novel", hint: "~24 ch" },
  { v: 40, label: "Epic", hint: "~40 ch" },
];
const PACING_OPTIONS = [
  { v: "slow", label: "Slow-burn", hint: "atmosphere & depth" },
  { v: "standard", label: "Standard", hint: "balanced" },
  { v: "fast", label: "Fast-paced", hint: "propulsive action" },
];
const INTENSITY_OPTIONS = [
  { v: 0, label: "Tame", hint: "off-page" },
  { v: 1, label: "Standard", hint: "balanced" },
  { v: 2, label: "Gritty", hint: "grounded" },
  { v: 3, label: "Unflinching", hint: "visceral" },
  { v: 4, label: "Brutal", hint: "full force" },
];
const HUMOUR_OPTIONS = [
  { v: 0, label: "None", hint: "straight" },
  { v: 1, label: "Dry", hint: "subtle wit" },
  { v: 2, label: "Light", hint: "warm" },
  { v: 3, label: "Playful", hint: "banter" },
  { v: 4, label: "Comic", hint: "full comedy" },
];

const DREAD_OPTIONS = [
  { v: 0, label: "Off", hint: "no horror" },
  { v: 1, label: "Eerie", hint: "quiet unease" },
  { v: 2, label: "Unsettling", hint: "creeping" },
  { v: 3, label: "Sinister", hint: "horror" },
  { v: 4, label: "Terrifying", hint: "full fear" },
];

export default function DraftGeneratorDialog({ open, onOpenChange, projectId, project, onProjectPatch, onDone }) {
  const [genre, setGenre] = useState("");
  const [pov, setPov] = useState("");
  const [tone, setTone] = useState("");
  const [pacing, setPacing] = useState("standard");
  const [intensity, setIntensity] = useState(1);
  const [humour, setHumour] = useState(1);
  const [dread, setDread] = useState(0);
  const [gorePass, setGorePass] = useState(true);
  const [doubleActs, setDoubleActs] = useState([]);   // [[nameA, nameB], ...]
  const [chars, setChars] = useState([]);              // character names for the pair pickers
  const [pairA, setPairA] = useState("");
  const [pairB, setPairB] = useState("");
  const [premise, setPremise] = useState("");
  const [target, setTarget] = useState("short");
  const [customWords, setCustomWords] = useState("");
  const [chaptersWanted, setChaptersWanted] = useState("");
  const [est, setEst] = useState(null);
  const [savingSettings, setSavingSettings] = useState(false);
  const [job, setJob] = useState(null); // {status,total,done,current,error}
  const pollRef = useRef(null);

  const fetchEstimate = useCallback((n) => {
    const q = n && Number(n) > 0 ? `?chapters=${Number(n)}` : "";
    api.get(`/projects/${projectId}/generate-draft/estimate${q}`).then((r) => setEst(r.data)).catch(() => {});
  }, [projectId]);

  useEffect(() => {
    if (!open) return;
    setGenre(project?.genre || "");
    setPov(project?.pov || "");
    setTone(project?.tone || "");
    setPacing(project?.pacing || "standard");
    setIntensity(project?.intensity ?? 1);
    setHumour(project?.humour ?? 1);
    setDread(project?.dread ?? 0);
    setGorePass(project?.gore_pass !== false);
    setDoubleActs(Array.isArray(project?.double_acts) ? project.double_acts : []);
    setPairA(""); setPairB("");
    api.get(`/projects/${projectId}/entities`).then((r) => {
      setChars((r.data || []).filter((e) => (e.type || "character") === "character" && e.name).map((e) => e.name));
    }).catch(() => setChars([]));
    setPremise(project?.premise || "");
    setChaptersWanted("");
    setCustomWords("");
    fetchEstimate();
    setJob(null);
    api.get(`/projects/${projectId}/generate-draft/status`).then((r) => { if (r.data?.status === "running" || r.data?.resumable) setJob(r.data); }).catch(() => {});
  }, [open, projectId, project, fetchEstimate]);

  const poll = useCallback(() => {
    if (pollRef.current) clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      try {
        const r = await api.get(`/projects/${projectId}/generate-draft/status`);
        setJob(r.data);
        if (r.data.status === "done" || r.data.status === "error") {
          clearInterval(pollRef.current); pollRef.current = null;
          if (r.data.status === "done") { toast.success("Your draft is ready — read it through and edit away!"); onDone?.(); }
          else toast.error(r.data.error || "Generation stopped early");
        }
      } catch { /* keep polling */ }
    }, 4000);
  }, [projectId, onDone]);

  useEffect(() => {
    if (open && job?.status === "running") poll();
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [open, job?.status, poll]);

  const saveSettings = async () => {
    setSavingSettings(true);
    try {
      await api.put(`/projects/${projectId}`, { genre: genre.trim(), pov: pov.trim(), tone: tone.trim(), pacing, intensity, gore_pass: gorePass, humour, dread, double_acts: doubleActs });
      onProjectPatch?.({ genre: genre.trim(), pov: pov.trim(), tone: tone.trim(), pacing, intensity, gore_pass: gorePass, humour, dread, double_acts: doubleActs });
      toast.success("Style settings saved");
    } catch { toast.error("Couldn't save settings"); } finally { setSavingSettings(false); }
  };

  const start = async () => {
    try {
      await api.put(`/projects/${projectId}`, { genre: genre.trim(), pov: pov.trim(), tone: tone.trim(), pacing, intensity, gore_pass: gorePass, humour, dread, double_acts: doubleActs });
      onProjectPatch?.({ genre: genre.trim(), pov: pov.trim(), tone: tone.trim(), pacing, intensity, gore_pass: gorePass, humour, dread, double_acts: doubleActs, premise: premise.trim() });
      const body = { target, premise };
      if (customWords && Number(customWords) > 0) body.words_per_chapter = Number(customWords);
      if (est?.approx && chaptersWanted && Number(chaptersWanted) > 0) body.target_chapters = Number(chaptersWanted);
      const r = await api.post(`/projects/${projectId}/generate-draft`, body);
      setJob({ status: "running", total: r.data.estimated_chapters, done: 0, current: "Starting…", error: "" });
      toast.success("Writing your draft…");
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : (d?.error === "out_of_credits" ? `Not enough credits (need ${d.needed})` : "Couldn't start generation"));
    }
  };

  const pickBookSize = (n) => { setChaptersWanted(String(n)); fetchEstimate(n); };

  const running = job?.status === "running";
  const pct = job?.total ? Math.round((job.done / job.total) * 100) : 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-[calc(100vw-1.5rem)] sm:w-full max-w-lg overflow-x-hidden" data-testid="draft-generator-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Wand2 className="w-5 h-5 text-primary" /> Generate a draft</DialogTitle>
        </DialogHeader>

        {running || job?.status === "done" || job?.resumable ? (
          <div className="space-y-4 py-2" data-testid="draft-progress">
            <p className="text-sm text-muted-foreground">
              {job.status === "done"
                ? "All chapters written — close this and read through your draft."
                : (job.resumable || job.stalled || job.status === "error")
                  ? `Generation paused on “${job.current}”${job.error ? ` — ${job.error}` : " — the server may have restarted mid-run"}. Tap Resume to carry on from where it left off; every chapter already written is kept.`
                  : `Writing “${job.current}”… you can keep this open or come back later.`}
            </p>
            <div className="h-2.5 w-full rounded-full bg-secondary overflow-hidden">
              <div className="h-full bg-primary transition-all duration-500" style={{ width: `${pct}%` }} data-testid="draft-progress-bar" />
            </div>
            <p className="text-xs text-muted-foreground" data-testid="draft-progress-label">{job.done} / {job.total} chapters {job.status === "done" ? "✓" : ""}</p>
            {job.status === "done" && (
              <Button className="w-full gap-2" onClick={() => onOpenChange(false)} data-testid="draft-done-close">
                <CheckCircle2 className="w-4 h-4" /> Read my draft
              </Button>
            )}
            {job?.resumable && (
              <Button className="w-full gap-2" onClick={start} data-testid="draft-resume">
                <Wand2 className="w-4 h-4" /> Resume generation
              </Button>
            )}
            {running && !job.resumable && (
              <Button variant="outline" className="w-full" onClick={() => onOpenChange(false)} data-testid="draft-close-running">
                Close — it’ll keep writing in the background
              </Button>
            )}
          </div>
        ) : (
          <div className="space-y-4 py-1 min-w-0 w-full">
            <p className="text-sm text-muted-foreground">
              This writes prose into your <b>empty chapters</b>, following your Blueprint. Anything you've already written is left untouched. It fills any gaps in your plan (acts, chapters, beats) as it goes.
            </p>
            <div className="space-y-3">
              <div>
                <label className="text-xs font-medium text-muted-foreground">What’s the story about? <span className="text-primary">(in a nutshell)</span></label>
                <Textarea value={premise} onChange={(e) => setPremise(e.target.value)} rows={3} data-testid="draft-premise" className="mt-1"
                  placeholder="e.g. A disgraced knight must escort the child empress across a poisoned kingdom to the one person who can cure her — his own exiled brother." />
                <p className="text-[10px] text-muted-foreground mt-1">The AI treats this as the backbone of the whole book — it plans acts/chapters around it and keeps every chapter on-story. Your chapter summaries and beats still steer each chapter.</p>
              </div>
              <div>
                <label className="text-xs font-medium text-muted-foreground">Genre</label>
                <Input value={genre} onChange={(e) => setGenre(e.target.value)} placeholder="e.g. gaslamp fantasy, cozy mystery" data-testid="draft-genre" className="mt-1" />
              </div>
              <div>
                <label className="text-xs font-medium text-muted-foreground">Point of view</label>
                <div className="mt-1 flex flex-wrap gap-1.5">
                  {POV_OPTIONS.map((o) => (
                    <button key={o} type="button" onClick={() => setPov(o)} data-testid={`draft-pov-${o.split(",")[0].replace(/\s+/g, "-").toLowerCase()}`}
                      className={`text-[11px] rounded-full px-2.5 py-1 border transition-colors ${pov === o ? "bg-primary text-primary-foreground border-primary" : "bg-background border-border text-muted-foreground hover:text-foreground"}`}>{o}</button>
                  ))}
                </div>
                <Input value={pov} onChange={(e) => setPov(e.target.value)} placeholder="…or type your own" data-testid="draft-pov-input" className="mt-2" />
              </div>
              <div>
                <label className="text-xs font-medium text-muted-foreground">Tone</label>
                <Input value={tone} onChange={(e) => setTone(e.target.value)} placeholder="e.g. tense and atmospheric, warm and witty" data-testid="draft-tone" className="mt-1" />
              </div>
              <div>
                <label className="text-xs font-medium text-muted-foreground">Pacing</label>
                <div className="mt-1 grid grid-cols-3 gap-1.5" data-testid="draft-pacing">
                  {PACING_OPTIONS.map((p) => (
                    <button key={p.v} type="button" onClick={() => setPacing(p.v)} data-testid={`draft-pacing-${p.v}`}
                      className={`rounded-lg border px-2 py-2 text-center transition-colors ${pacing === p.v ? "bg-primary/10 border-primary" : "bg-background border-border hover:border-primary/50"}`}>
                      <div className="text-xs font-medium">{p.label}</div>
                      <div className="text-[10px] text-muted-foreground">{p.hint}</div>
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className="text-xs font-medium text-muted-foreground">Content intensity <span className="text-muted-foreground/70">(how graphic violence, injury &amp; death get)</span></label>
                <div className="mt-1 grid grid-cols-3 sm:grid-cols-5 gap-1.5" data-testid="draft-intensity">
                  {INTENSITY_OPTIONS.map((it) => (
                    <button key={it.v} type="button" onClick={() => setIntensity(it.v)} data-testid={`draft-intensity-${it.v}`}
                      className={`rounded-lg border px-1 py-2 text-center transition-colors ${intensity === it.v ? "bg-primary/10 border-primary" : "bg-background border-border hover:border-primary/50"}`}>
                      <div className="text-[11px] font-medium leading-tight">{it.label}</div>
                      <div className="text-[9px] text-muted-foreground">{it.hint}</div>
                    </button>
                  ))}
                </div>
                <p className="text-[10px] text-muted-foreground mt-1">Only affects scenes that already involve violence or injury — it won't add gore to calm scenes.</p>
                {intensity === 4 && (
                  <div className="mt-2 flex items-start justify-between gap-3 rounded-lg border border-red-500/30 bg-red-500/5 px-3 py-2" data-testid="draft-gore-pass">
                    <div className="min-w-0">
                      <div className="text-xs font-medium">Gore pass</div>
                      <div className="text-[10px] text-muted-foreground">A second AI pass rewrites only the violent paragraphs at full gruesome detail. Off = cheaper &amp; faster, still Brutal.</div>
                    </div>
                    <Switch checked={gorePass} onCheckedChange={setGorePass} data-testid="draft-gore-pass-toggle" className="mt-0.5" />
                  </div>
                )}
              </div>
              <div>
                <label className="text-xs font-medium text-muted-foreground">Humour &amp; wit <span className="text-muted-foreground/70">(dry wit, banter &amp; levity)</span></label>
                <div className="mt-1 grid grid-cols-3 sm:grid-cols-5 gap-1.5" data-testid="draft-humour">
                  {HUMOUR_OPTIONS.map((it) => (
                    <button key={it.v} type="button" onClick={() => setHumour(it.v)} data-testid={`draft-humour-${it.v}`}
                      className={`rounded-lg border px-1 py-2 text-center transition-colors ${humour === it.v ? "bg-primary/10 border-primary" : "bg-background border-border hover:border-primary/50"}`}>
                      <div className="text-[11px] font-medium leading-tight">{it.label}</div>
                      <div className="text-[9px] text-muted-foreground">{it.hint}</div>
                    </button>
                  ))}
                </div>
                <p className="text-[10px] text-muted-foreground mt-1">Shapes character-driven wit and banter. A comedic tone or genre nudges it up on its own.</p>
              </div>
              <div>
                <label className="text-xs font-medium text-muted-foreground">Dread <span className="text-muted-foreground/70">(how sinister &amp; scary it feels)</span></label>
                <div className="mt-1 grid grid-cols-3 sm:grid-cols-5 gap-1.5" data-testid="draft-dread">
                  {DREAD_OPTIONS.map((it) => (
                    <button key={it.v} type="button" onClick={() => setDread(it.v)} data-testid={`draft-dread-${it.v}`}
                      className={`rounded-lg border px-1 py-2 text-center transition-colors ${dread === it.v ? "bg-primary/10 border-primary" : "bg-background border-border hover:border-primary/50"}`}>
                      <div className="text-[11px] font-medium leading-tight">{it.label}</div>
                      <div className="text-[9px] text-muted-foreground">{it.hint}</div>
                    </button>
                  ))}
                </div>
                <p className="text-[10px] text-muted-foreground mt-1">Fear craft: withheld threats, wrongness in the ordinary, rising tension, uneasy chapter endings. A horror genre or tone raises it to at least Sinister. Pairs with Intensity for gore.</p>
              </div>
              {chars.length >= 2 && (
                <div data-testid="draft-double-acts">
                  <label className="text-xs font-medium text-muted-foreground">Double acts <span className="text-muted-foreground/70">(pairs who trade quips back and forth)</span></label>
                  {doubleActs.length > 0 && (
                    <div className="mt-1 flex flex-wrap gap-1.5">
                      {doubleActs.map((p, i) => (
                        <span key={i} data-testid={`draft-double-act-${i}`} className="inline-flex items-center gap-1 rounded-full border border-fuchsia-500/40 bg-fuchsia-500/10 text-fuchsia-300 px-2 py-0.5 text-[11px]">
                          {(p || []).join(" & ")}
                          <button type="button" onClick={() => setDoubleActs((a) => a.filter((_, idx) => idx !== i))} data-testid={`draft-double-act-remove-${i}`} className="hover:text-fuchsia-100"><X className="w-3 h-3" /></button>
                        </span>
                      ))}
                    </div>
                  )}
                  <div className="mt-1.5 flex items-center gap-1.5">
                    <select value={pairA} onChange={(e) => setPairA(e.target.value)} data-testid="draft-pair-a" className="h-8 rounded-md border border-border bg-background px-2 text-xs flex-1 min-w-0 w-0">
                      <option value="">Character…</option>
                      {chars.map((n) => <option key={n} value={n}>{n}</option>)}
                    </select>
                    <span className="text-xs text-muted-foreground">&amp;</span>
                    <select value={pairB} onChange={(e) => setPairB(e.target.value)} data-testid="draft-pair-b" className="h-8 rounded-md border border-border bg-background px-2 text-xs flex-1 min-w-0 w-0">
                      <option value="">Character…</option>
                      {chars.map((n) => <option key={n} value={n}>{n}</option>)}
                    </select>
                    <Button type="button" size="sm" variant="outline" className="h-8 text-xs shrink-0" data-testid="draft-pair-add"
                      disabled={!pairA || !pairB || pairA === pairB || doubleActs.some((p) => (p || []).includes(pairA) && (p || []).includes(pairB))}
                      onClick={() => { setDoubleActs((a) => [...a, [pairA, pairB]]); setPairA(""); setPairB(""); }}>Pair</Button>
                  </div>
                  <p className="text-[10px] text-muted-foreground mt-1">When both share a scene, they'll bounce off each other. Needs humour on.</p>
                </div>
              )}
              <div>
                <label className="text-xs font-medium text-muted-foreground">Chapter length</label>
                <div className="mt-1 grid grid-cols-3 gap-1.5">
                  {LENGTHS.map((l) => (
                    <button key={l.v} type="button" onClick={() => { setTarget(l.v); setCustomWords(""); }} data-testid={`draft-length-${l.v}`}
                      className={`rounded-lg border px-2 py-2 text-center transition-colors ${target === l.v && !customWords ? "bg-primary/10 border-primary" : "bg-background border-border hover:border-primary/50"}`}>
                      <div className="text-xs font-medium">{l.label}</div>
                      <div className="text-[10px] text-muted-foreground">{l.hint}</div>
                    </button>
                  ))}
                </div>
                <div className="mt-2 flex items-center gap-2">
                  <span className="text-[11px] text-muted-foreground whitespace-nowrap">or custom</span>
                  <Input type="number" min="100" step="50" value={customWords} onChange={(e) => setCustomWords(e.target.value)}
                    placeholder="words per chapter (e.g. 2000)" data-testid="draft-custom-words" className="h-8 text-xs" />
                </div>
              </div>

              {est?.approx && (
                <div data-testid="draft-book-size">
                  <label className="text-xs font-medium text-muted-foreground">Book length</label>
                  <p className="text-[10px] text-muted-foreground mt-0.5">Your Blueprint has no chapters laid out yet — choose how big the book should be and the AI will outline and write that many.</p>
                  <div className="mt-1 grid grid-cols-4 gap-1.5">
                    {BOOK_SIZES.map((b) => (
                      <button key={b.v} type="button" onClick={() => pickBookSize(b.v)} data-testid={`draft-booksize-${b.v}`}
                        className={`rounded-lg border px-1.5 py-2 text-center transition-colors ${Number(chaptersWanted) === b.v ? "bg-primary/10 border-primary" : "bg-background border-border hover:border-primary/50"}`}>
                        <div className="text-xs font-medium">{b.label}</div>
                        <div className="text-[10px] text-muted-foreground">{b.hint}</div>
                      </button>
                    ))}
                  </div>
                  <div className="mt-2 flex items-center gap-2">
                    <span className="text-[11px] text-muted-foreground whitespace-nowrap">or exactly</span>
                    <Input type="number" min="1" step="1" value={chaptersWanted}
                      onChange={(e) => setChaptersWanted(e.target.value)} onBlur={(e) => fetchEstimate(e.target.value)}
                      placeholder="number of chapters" data-testid="draft-chapters-input" className="h-8 text-xs" />
                  </div>
                </div>
              )}
            </div>

            {est && (
              <div className="flex items-center justify-between rounded-lg border border-border bg-secondary/30 px-3 py-2" data-testid="draft-estimate">
                <span className="text-xs text-muted-foreground flex items-center gap-1.5"><Coins className="w-3.5 h-3.5" />
                  {est.chapters_to_write} chapter{est.chapters_to_write === 1 ? "" : "s"}{est.approx ? " (approx)" : ""} to write
                </span>
                <span className="text-sm font-semibold">{est.est_credits} credits</span>
              </div>
            )}

            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={saveSettings} disabled={savingSettings} data-testid="draft-save-settings" className="gap-1.5">
                {savingSettings ? <Loader2 className="w-4 h-4 animate-spin" /> : null} Save style
              </Button>
              <Button className="flex-1 gap-2" onClick={start} disabled={!est || est.chapters_to_write < 1} data-testid="draft-start">
                <Sparkles className="w-4 h-4" /> {est && est.chapters_to_write < 1 ? "Nothing empty to write" : "Generate draft"}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
